import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { UploadIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { API, constructApiUrl } from '../../../../../../api/apis.config'
import { getCentreEndOfJourneyReviews } from '../../../../../../api/end-of-journey-reviews/centre/get-centre-end-of-journey-reviews'
import {
  CentreReviewYouth,
  ReviewColumnAggregate
} from '../../../../../../api/end-of-journey-reviews/centre/types'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import { downloadDocuments } from '../../../../../../lib/utils/files/download-documents'
import { showAxiosError } from '../../../../../../lib/utils/functions/misc.functions'
import ReviewRatingPill, {
  formatReviewNumber,
  receptionStatusVariant,
  ReviewLoadError
} from './review-rating-pill'

type Props = {
  centerId: number
}

type Row = Omit<CentreReviewYouth, 'enrollmentId' | 'questionnaireReceived'> & {
  id: number
  order: number | null
  // the "% de réponse" and "Moyenne" rows above the youths
  aggregate?: 'responseRate' | 'average'
  rowColor?: TailwindColor
}

const aggregateText = (aggregate: ReviewColumnAggregate | undefined, kind: Row['aggregate']) => {
  if (!aggregate) return null
  if (kind === 'responseRate')
    return aggregate.responseRate === null
      ? null
      : `${formatReviewNumber(aggregate.responseRate)} %`
  return aggregate.average === null
    ? null
    : `${formatReviewNumber(aggregate.average)}/${aggregate.scaleMax}`
}

/** "Appréciation des jeunes" tab: each youth's end-of-stay ratings, back office only. */
const CenterEndOfStayAppreciations = ({ centerId }: Props) => {
  const { t } = useTranslate()
  const { data, isPending, error } = useQuery({
    queryKey: ['centre-end-of-journey-reviews', centerId],
    queryFn: () => getCentreEndOfJourneyReviews(centerId)
  })

  const { mutate: exportExcel, isPending: isExporting } = useMutation({
    mutationFn: () =>
      downloadDocuments({
        endpoint: constructApiUrl(API.centers.exportEndOfJourneyReviewsExcel.url, {
          id: centerId
        })
      }),
    onError: showAxiosError
  })

  const rows = useMemo((): Row[] => {
    if (!data) return []
    const emptyRow = {
      firstName: '',
      age: null,
      gender: null,
      order: null,
      ratings: Object.fromEntries(data.columns.map(({ code }) => [code, null])) as Row['ratings']
    }
    const aggregateRow = (id: number, kind: Row['aggregate'], label: string): Row => ({
      ...emptyRow,
      id,
      lastName: label,
      aggregate: kind,
      // light orange with orange pills, as the totals of the summary page
      rowColor: tailwindColors.secondary.light.DEFAULT
    })

    return [
      aggregateRow(-1, 'responseRate', t('center.end.of.stay.appreciations.response.rate')),
      aggregateRow(-2, 'average', t('center.end.of.stay.appreciations.average')),
      ...data.youths.map(({ enrollmentId, questionnaireReceived: _, ...youth }, index) => ({
        ...youth,
        id: enrollmentId,
        order: index + 1
      }))
    ]
  }, [data, t])

  const columns = useMemo((): Column<Row>[] => {
    if (!data) return []
    const aggregates = new Map(data.aggregates.map((aggregate) => [aggregate.code, aggregate]))

    return [
      {
        header: t('center.end.of.stay.appreciations.columns.youths'),
        // the group's own id, "#" below is the order column
        accessorKey: 'youths',
        columns: [
          { header: '#', accessorKey: 'order', cell: ({ row }) => row.original.order ?? '' },
          {
            header: t('center.end.of.stay.appreciations.columns.last.name'),
            accessorKey: 'lastName',
            cell: ({ row }) =>
              row.original.aggregate ? (
                <Typography variant="label-semibold" color={tailwindColors.secondary.DEFAULT}>
                  {row.original.lastName}
                </Typography>
              ) : (
                row.original.lastName
              )
          },
          {
            header: t('center.end.of.stay.appreciations.columns.first.name'),
            accessorKey: 'firstName'
          },
          {
            header: t('center.end.of.stay.appreciations.columns.age'),
            accessorKey: 'age',
            cell: ({ row }) => row.original.age ?? ''
          },
          {
            header: t('center.end.of.stay.appreciations.columns.gender'),
            accessorKey: 'gender',
            cell: ({ row }) => row.original.gender ?? ''
          }
        ]
      },
      {
        header: t('center.end.of.stay.appreciations.columns.questions'),
        accessorKey: 'ratings',
        // DataTable puts the parent's classes on its first child column: the group separator
        cellClassName: 'border-l border-l-grey-light-hover',
        columnHeaderClassName: 'border-l border-l-grey-light-hover',
        columns: data.columns.map(
          ({ code, shortLabel }): Column<Row> => ({
            header: shortLabel,
            accessorKey: `ratings.${code}` as Column<Row>['accessorKey'],
            cell: ({ row }) => {
              const { aggregate, ratings } = row.original
              if (aggregate)
                return (
                  <ReviewRatingPill
                    variant="warning"
                    color={tailwindColors.secondary.light.active}
                    text={aggregateText(aggregates.get(code), aggregate)}
                  />
                )

              const rating = ratings[code]
              return <ReviewRatingPill text={rating && `${rating.rating}/${rating.scaleMax}`} />
            }
          })
        )
      }
    ]
  }, [data, t])

  return (
    <div className="mt-5">
      <div className="flex items-center gap-3">
        <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
          {t('center.end.of.stay.appreciations.title')}
        </Typography>
        {data && (
          <Badge
            variant={receptionStatusVariant[data.receptionStatus]}
            text={t('center.end.of.stay.appreciations.received', {
              received: data.received,
              enrolled: data.enrolled
            })}
          />
        )}
      </div>
      <div className="h-3" />
      <Button
        type="button"
        variant="secondary"
        icon={UploadIcon}
        label={t('center.end.of.stay.appreciations.export')}
        loading={isExporting}
        disabled={!data}
        onClick={() => exportExcel()}
      />
      <div className="h-3" />
      {isPending ? (
        <Loader center />
      ) : error ? (
        <ReviewLoadError error={error} />
      ) : (
        <DataTable<Row>
          manualSorting={false}
          columns={columns}
          data={rows}
          rowCount={rows.length}
          paginated={false}
        />
      )}
    </div>
  )
}

export default CenterEndOfStayAppreciations
